import {
  type ScriptAnalysis,
  scriptAnalysisSchema,
  scriptUploadResponseSchema,
} from '@noconflict/contracts';
import { z } from 'zod';
import { apiFetch } from '../../shared/api/http';
import type { Supabase } from '../../shared/api/supabase';

/**
 * The project's own analysis, the original script's (RLS: whoever may read the project), or null when it has no
 * script. Later versions have analyses of their own, which never price the order or fill the form.
 */
export async function getScriptAnalysis(
  client: Supabase,
  projectId: string,
): Promise<ScriptAnalysis | null> {
  const { data, error } = await client
    .rpc('project_script_analysis', { p_project_id: projectId })
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ? scriptAnalysisSchema.parse(data) : null;
}

/**
 * Tells the worker the script object is uploaded. It registers, measures and reads it, and answers once the
 * form can open (a few seconds). A script that cannot be used is refused with a code and nothing of it is kept.
 */
export const analyseScript = (
  token: string,
  projectId: string,
  file: { fileId: string; filename: string },
): Promise<ScriptAnalysis> =>
  apiFetch(`/api/projects/${projectId}/script`, scriptUploadResponseSchema, {
    method: 'POST',
    token,
    body: JSON.stringify(file),
  }).then((r) => r.analysis);

/** Removes the project's script, its analysis and what was read from it. */
export async function removeScript(token: string, projectId: string): Promise<void> {
  await apiFetch(`/api/projects/${projectId}/script`, z.unknown(), { method: 'DELETE', token });
}
